'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { ThemeProvider } from '@/lib/theme/ThemeProvider';
import { TenantProvider } from '@/contexts/TenantContext';
import { useProactiveTokenRefresh } from '@/lib/auth/useProactiveTokenRefresh';

export function Providers({ children }: { children: React.ReactNode }) {
  // App-wide, mounted once: silently keeps the access token fresh in the
  // background so mid-task actions (e.g. a long Work Order extraction
  // review) never trip over a stale token. See the hook's own comment for
  // why this is needed in addition to the existing reactive 401 refresh.
  useProactiveTokenRefresh();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TenantProvider>
          {children}
        </TenantProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
